import React, { useEffect, useState } from 'react';

import { AtSymbolOutline, Box, HashtagOutline, PlusOutline, rem } from '@bonusly/design-system';
import { useSpeechTags } from '../hooks/use-speech-tags';
import { TranscriptResponse } from '../lib/entities/speech-tags';

interface SpeechTagCirclesProps {
	getSpeechTags: () => Promise<any>;
	isRecording: boolean;
	shouldClear?: boolean;
}

export const SpeechTagCircles: React.FC<SpeechTagCirclesProps> = ({ getSpeechTags, isRecording, shouldClear = false }) => {
	const [currentSpeechTags, setCurrentSpeechTags] = useState<TranscriptResponse | null>(null);
	const speechTagStates = useSpeechTags(currentSpeechTags);

	// Clear state only when explicitly requested
	useEffect(() => {
		if (shouldClear) {
			setCurrentSpeechTags(null);
		}
	}, [shouldClear]);

	// Fetch speech tags periodically when recording
	useEffect(() => {
		if (!isRecording) {
			return;
		}

		const interval = setInterval(async () => {
			try {
				const speechTags = await getSpeechTags();
				// Only update state if the data has actually changed
				setCurrentSpeechTags((prevTags) => {
					// If both are null/undefined, no change needed
					if (!speechTags && !prevTags) return prevTags;

					// If one is null/undefined and the other isn't, update needed
					if (!speechTags || !prevTags) return speechTags;

					// Deep comparison of the relevant fields
					const prevUsers = prevTags.matched_users || [];
					const newUsers = speechTags.matched_users || [];
					const prevSpeechTags = prevTags.speech_tags || {};
					const newSpeechTags = speechTags.speech_tags || {};

					// Check if any values have changed
					const usersChanged = prevUsers.length !== newUsers.length || !prevUsers.every((user: string, index: number) => user === newUsers[index]);
					const pointsChanged = !!prevSpeechTags.points !== !!newSpeechTags.points;
					const hashtagsChanged = (prevSpeechTags.hashtags?.length || 0) !== (newSpeechTags.hashtags?.length || 0);
					const namesChanged = (prevSpeechTags.names?.length || 0) !== (newSpeechTags.names?.length || 0);

					// Only update if something actually changed
					if (usersChanged || pointsChanged || hashtagsChanged || namesChanged) {
						return speechTags;
					}

					return prevTags;
				});
			} catch (error) {
				console.error('Error getting speech tags:', error);
			}
		}, 600);

		return () => clearInterval(interval);
	}, [isRecording, getSpeechTags]);

	return (
		<Box position="absolute" top="16px" right="16px" zIndex={2} display="flex" gap="8px">
			<Box
				width={rem(32)}
				height={rem(32)}
				borderRadius="full"
				backgroundColor={speechTagStates.mentions ? 'primary.fg' : 'bg.subtle'}
				display="flex"
				alignItems="center"
				justifyContent="center"
				boxShadow={speechTagStates.mentions ? '0 0 0 3px rgba(21, 115, 230, 0.3)' : '0 2px 8px rgba(0,0,0,0.08)'}
			>
				<AtSymbolOutline size={18} color={speechTagStates.mentions ? 'text.onColor' : 'text.disabled'} />
			</Box>
			<Box
				width={rem(32)}
				height={rem(32)}
				borderRadius="full"
				backgroundColor={speechTagStates.points ? 'points.fg' : 'bg.subtle'}
				display="flex"
				alignItems="center"
				justifyContent="center"
				boxShadow={speechTagStates.points ? '0 0 0 3px rgba(11, 107, 67, 0.3)' : '0 2px 8px rgba(0,0,0,0.08)'}
			>
				<PlusOutline size={18} color={speechTagStates.points ? 'text.onColor' : 'text.disabled'} />
			</Box>
			<Box
				width={rem(32)}
				height={rem(32)}
				borderRadius="full"
				backgroundColor={speechTagStates.hashtags ? 'hashtag.fgOnSurface' : 'bg.subtle'}
				display="flex"
				alignItems="center"
				justifyContent="center"
				boxShadow={speechTagStates.hashtags ? '0 0 0 3px rgba(124, 58, 237, 0.07)' : '0 2px 8px rgba(0,0,0,0.08)'}
			>
				<HashtagOutline size={18} color={speechTagStates.hashtags ? 'text.onColor' : 'text.disabled'} />
			</Box>
		</Box>
	);
};
